"use client";

import type { CalendarData, Slot } from "@/features/calendar/types/calendar.ts";

import { useCallback, useEffect, useState } from "react";
import { useMediaQuery } from "react-responsive";
import { useTranslation } from "react-i18next";
import { useDebouncedCallback } from "use-debounce";
import { endOfWeek, format, parseISO, startOfWeek } from "date-fns";

import { useCalendarModalStore } from "../store/useCalendarModalStore";

import { useCalendarStore } from "@/features/calendar/store/useCalendarStore.tsx";
import { axiosApiInstance } from "@/libs/axios.tsx";
import { API_ENDPOINTS, WORKSPACES_API_ENDPOINTS } from "@/constants/api.ts";
import CalendarModal from "@/features/calendar/components/CalendarModal.tsx";
import StudentCalendarView from "@/features/calendar/views/StudentCalendarView.tsx";
import TutorCalendarView from "@/features/calendar/views/TutorCalendarView.tsx";
import WorkspaceForbidden from "@/features/workspaces/components/WorkspaceForbidden.tsx";

// Extend the Slot type to include visual rendering properties
type ExtendedSlot = Slot & {
  visualStartTime?: string;
  visualEndTime?: string;
};

interface Props {
  workspaceId?: string;
  canMakeReservations?: boolean;
}

function CalendarPage({ workspaceId, canMakeReservations }: Props) {
  const {
    setCalendarData,
    setShownDays,
    dateRange,
    setIsLoading,
    toReload,
    setToReload,
    setDateRange,
  } = useCalendarStore();
  const { t, i18n } = useTranslation();
  const [error, setError] = useState<any | null>(null);
  const isMobile = useMediaQuery({ maxWidth: 768 });
  const isWideScreen = useMediaQuery({ minWidth: 1500 });
  const { setIsOpen } = useCalendarModalStore();

  const getShownDays = useCallback(() => {
    if (isMobile) return 1;
    if (isWideScreen) return 7;

    return 3;
  }, [isMobile, isWideScreen]);

  useEffect(() => {
    return () => {
      setDateRange({
        from: format(startOfWeek(new Date(), { weekStartsOn: 1 }), "yyyy-MM-dd"),
        to: format(endOfWeek(new Date(), { weekStartsOn: 1 }), "yyyy-MM-dd"),
      });
      setIsOpen(false);
    };
  }, []);

  useEffect(() => {
    setShownDays(getShownDays());
  }, [getShownDays, setShownDays]);

  // Process slots to split overnight slots into two separate slots
  const processOvernightSlots = (data: CalendarData): CalendarData => {
    if (!data || !data.data) return data;

    const processedSlots: ExtendedSlot[] = [];

    data.data.forEach((slot) => {
      // Get the start and end dates from the datetime strings
      const startDate = parseISO(slot.startTimeDateTimeString);
      const endDate = parseISO(slot.endTimeDateTimeString);

      // Check if the dates are different (overnight slot)
      const startDateStr = format(startDate, "yyyy-MM-dd");
      const endDateStr = format(endDate, "yyyy-MM-dd");

      if (startDateStr !== endDateStr) {
        // Create first part of the slot (from start time to midnight)
        const firstSlot: ExtendedSlot = {
          ...slot,
          id: `${slot.id}-part1`,
          visualEndTime: "23:59",
        };

        // Create second part of the slot (from midnight to end time)
        const secondSlot: ExtendedSlot = {
          ...slot,
          id: `${slot.id}-part2`,
          visualStartTime: "00:00",
          startTimeDateTimeString: format(
            new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate(), 0, 0),
            "yyyy-MM-dd'T'HH:mm:ss",
          ),
        };

        processedSlots.push(firstSlot, secondSlot);
      } else {
        // If dates are the same but time indicates overnight, it's a special case
        // Just add the original slot for now
        processedSlots.push(slot);
      }
    });

    return {
      ...data,
      data: processedSlots as Slot[],
    };
  };

  async function getCalendars() {
    try {
      setIsLoading(true);
      const { data: response } = await axiosApiInstance.get<CalendarData>(
        `${workspaceId ? WORKSPACES_API_ENDPOINTS(workspaceId).STUDENT.SCHEDULE : API_ENDPOINTS.TUTOR.CALENDAR.GET_DATES}?start_date=${dateRange?.from}&end_date=${dateRange?.to}`,
      );

      // Process the data to split overnight slots
      const processedData = processOvernightSlots(response);

      console.log(processedData);

      setCalendarData(processedData);
    } catch (err) {
      setError(err);
    } finally {
      toReload && setToReload(false);
      setIsLoading(false);
    }
  }

  const debouncedGetCalendars = useDebouncedCallback(getCalendars, 100);

  useEffect(() => {
    debouncedGetCalendars();
  }, [dateRange, i18n.language]);

  useEffect(() => {
    if (!toReload) return;
    debouncedGetCalendars();
  }, [toReload]);

  useEffect(() => {
    setError(null);
  }, []);

  if (error) {
    if (error.response.data.code === "user_left_workspace") {
      return <WorkspaceForbidden />;
    }

    return (
      <div className={"flex h-full w-full items-center justify-center"}>
        <h3 className={"font-bold"}>{t("calendar.errorMessage")}</h3>
      </div>
    );
  }

  const isStudent = !!workspaceId;

  return (
    <div className="bg-background laptop:w-[calc(100vw_-_4rem)] flex h-full max-h-[calc(100dvh_-_4rem)] w-screen flex-col items-center overflow-hidden min-[1650px]:flex-row">
      <title>{`${t("calendar.calendar")} - Ximly`}</title>

      <div className="h-full w-full pe-2">
        {isStudent ? (
          <StudentCalendarView
            canMakeReservations={canMakeReservations}
            config={{
              hourHeight: 80,
              headerHeight: 60,
            }}
          />
        ) : (
          <TutorCalendarView
            config={{
              hourHeight: 80,
              headerHeight: 60,
            }}
          />
        )}
      </div>
      <CalendarModal />
    </div>
  );
}

export default CalendarPage;
